import { COMMON_STYLES, SimplePostCard } from '@dehaat/dds';
import fetchSuggestedProductsOnPost, { SuggestedPostsApiResponse } from '@dehaat/kisan-app-bl/api/fetchSuggestedProductsOnPost';
import followUnfollowChannel from '@dehaat/kisan-app-bl/api/followUnfollowPostEntity';
import likeDislikePost from '@dehaat/kisan-app-bl/api/likeDislikePostV2';
import { Tags, UserPosts } from '@dehaat/kisan-app-bl/models/PostsV2';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import BottomDrawer from '@/components/BottomDrawer';
import ErrorModal from '@/components/ErrorModal';
import LoginDrawer from '@/components/LoginDrawer';
import Okay from '@/components/modalActions/Okay';
import MediaCardV2 from '@/components/posts/MediaCardV2';
import UserVerifiedImage from '@/components/posts/UserVerifiedImage';
import { POST_TYPE } from '@/constants/posts';
import { POST_TYPE as POST_TYPEV2 } from '@/constants/postv2';
import { AuthContext } from '@/context/AuthProvider';
import useErrorModalState from '@/hooks/useErrorModalState';
import useTextToSpeech from '@/hooks/useTextToSpeech';
import useTriggerEvents from '@/hooks/useTriggerEvents';
import styles from '@/styles/DehaatFeed.module.css';
import CustomEventTypes from '@/utils/analytics/customEventTypes';
// import { trackCustomEvent } from '@/utils/analytics/googleAnalytics'
import { isArray, isProductionBuild } from '@/utils/helper';
import { relativeTime } from '@/utils/helpers/getDate';
import getWhatsappShareURL from '@/utils/helpers/getWhatsappShareURL';
interface Props extends UserPosts {
  commentCount: number;
  onCommentClick: VoidFunction;
}
const DehaatFeedCard = ({
  id,
  is_liked,
  like_count,
  commentCount,
  created_at,
  content,
  head,
  attachments,
  tags,
  name,
  slug,
  onCommentClick,
  post_type
}: Props) => {
  const {
    t
  } = useTranslation('posts');
  const {
    isAuthenticated
  } = useContext(AuthContext);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const {
    locale,
    push,
    asPath
  } = useRouter();
  const {
    isLanguageSupported,
    speak
  } = useTextToSpeech(locale || 'hi');
  const [likeCount, setLikeCount] = useState(like_count || 0);
  const [isLiked, setIsLiked] = useState(is_liked || false);
  const [isFollowed, setIsFollowed] = useState(head?.is_followed);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [showListenPrompt, setShowListenPrompt] = useState(true); // should be visible in the beginning
  const [href, setHref] = useState('');
  const {
    trackCustomEvent
  } = useTriggerEvents();
  const {
    errorModalState,
    hideErrorModal,
    setErrorModalState
  } = useErrorModalState();
  const toggleLikeBtn = async () => {
    if (!isAuthenticated) {
      toggleDrawer();
      return;
    }
    const result = await likeDislikePost(id, POST_TYPEV2.Blog);
    if (result) {
      if (isLiked) {
        setLikeCount(prev => prev - 1);
      } else {
        setLikeCount(prev => prev + 1);
      }
      setIsLiked(prev => !prev);
    } else {
      setErrorModalState({
        ...errorModalState,
        show: true,
        allowClose: true
      });
    }
  };
  const toggleFollowBtn = async () => {
    if (!isAuthenticated) {
      toggleDrawer();
      return;
    }
    const result = await followUnfollowChannel(head!.follow_entity.id, 'CHANNEL', isFollowed ? 'unfollow' : 'follow');
    if (result) {
      setIsFollowed(prev => !prev);
    } else {
      setErrorModalState({
        ...errorModalState,
        show: true,
        allowClose: true
      });
    }
  };
  const handleShare = (postType = '') => {
    const eventType = postType == POST_TYPE.Community ? CustomEventTypes.COMMUNITY_POST_SHARED : CustomEventTypes.DEHAAT_FEED_SHARED;
    trackCustomEvent(eventType, isArray(tags) ? JSON.stringify(tags) : undefined);
    const hRef = href ? getWhatsappShareURL(href, slug, 'BLOG') : undefined;
    window.open(hRef);
  };
  const handleTagClick = (tag: Tags) => {
    const {
      slug
    } = tag.slug;
    push(`/blogs/tag/${slug}`);
  };
  const handleChannelClick = () => {
    if (asPath.includes('/blogs/channel')) {
      return;
    }
    const {
      slug
    } = head!.slug;
    push(`/blogs/channel/${slug}`);
  };
  const handleCardClick = () => {
    push(`/blog/${slug}`);
  };
  const toggleDrawer = () => {
    setIsDrawerOpen(prev => !prev);
  };
  const handleTextToSpeech = () => {
    setShowListenPrompt(prev => !prev);
    if (contentRef && contentRef.current) {
      speak(contentRef.current.innerText);
    }
  };
  const appendSuggestedProducts = (suggestedProducts: SuggestedPostsApiResponse) => {
    if (contentRef && contentRef.current) {
      const r = /\[\[product_name=([^\]]+)\]\]/g;
      contentRef.current.innerHTML = contentRef.current.innerHTML.replace(r, (_, productName: string) => {
        const product = suggestedProducts.data.find(productData => productData.products[0].name.toLowerCase() === productName.toLowerCase());
        if (!product) return ``;
        const {
          cta,
          id,
          image_url,
          name,
          pav,
          price,
          product_deeplink
        } = product.products[0];
        const productIdRegex = /productId=(\d+)/;
        const match = product_deeplink.match(productIdRegex);
        const productId = match ? match[1] : id;

        // Ideally this should be rendered as a JSX element and then injected
        // into the HTML by using renderToString from react-dom/server.
        // However, doing that increases the bundle size for all pages
        // by 20kiB. Hence, using strings directly instead.
        return `<div class="my-2 -mr-2 -ml-8">
            <h6 class="bg-secondary-two-110 py-3 font-nato-semibold text-white text-lg text-center">
           ${suggestedProducts.title.name}
           </h6>
            <div class="bg-warning-10 px-4 py-4">
            <div class="bg-white rounded-xl">
           <div class="block ml-4 pr-4 border border-x-transparent border-t-neutral-10 border-b-transparent first:border-none">
           <section class="flex items-center pt-4 pb-5">
           <div class="flex flex-col items-center">
           <div class="relative flex justify-center items-center p-2 border border-neutral-10 rounded w-20 h-20 aspect-square shrink-0 grow-0">
           <img class="" alt=${name} loading="lazy" decoding="async" data-nimg="1" src=${image_url}>
           </div>
           </div>
           <div class="ml-4">
           <p class="mb-1 font-nato-medium text-neutral-50 text-xs">
           ${product.products[0].brand_name || ''}
           </p>
           <p class="font-nato-medium text-neutral-90 text-base">
          ${name}
           </p>
           <div class="flex items-center mt-1">
           <span class="font-nato-semibold text-neutral-80 text-base leading-5">
           <span class="inline-block text-[8px] align-super">₹</span>${price - (product.products[0].discount || 0)}</span>
           </div>
           <div class="flex items-center gap-3 mt-2">
           <div class="flex flex-1 justify-center items-center gap-2 py-2.5 border border-neutral-20 rounded-lg min-w-[76px]">
           <span class="font-nato-semibold text-primary-100 text-sm leading-4.5">
          ${pav}</span>
           </div>
           <a href="/products/${productId}?from=posts" target="_blank" class="flex flex-1 justify-center items-center bg-primary-100 py-2.5 rounded-lg min-w-[76px]">
           <svg viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg" height="20" width="20" class="fill-transparent stroke-white"><path d="M2.5 8h11M8 2.5v11" stroke-linecap="round" stroke-linejoin="round"></path></svg>
           <span class="ml-2 font-nato-semibold text-white text-sm leading-4.5">${cta.text}</span>
           <a>
           </div>
           </div>
           </section>
           </div>
            </div>
            </div>
            </div>`;
      });
    }
  };

  //If user is authenticated the states neeed to be updated as per need
  useEffect(() => {
    if (isAuthenticated) {
      setIsLiked(is_liked);
    }
  }, [isAuthenticated, is_liked]);
  useLayoutEffect(() => {
    const getSuggestedProducts = async () => {
      try {
        const products = await fetchSuggestedProductsOnPost(id, 'BLOG_POST', isProductionBuild ? 1509 : 343,
        // Default values for vendor id where all suggested products are available
        locale);
        if (products) {
          appendSuggestedProducts(products);
        }
      } catch {}
    };
    getSuggestedProducts();
  }, [id, locale]);
  useEffect(() => {
    setHref(`${location.origin}/blog/${slug}`);
  }, [slug]);
  return <div className="mx-auto pb-0 w-full max-w-4xl" data-sentry-component="DehaatFeedCard" data-sentry-source-file="DehaatFeedCard.tsx">
      {isLanguageSupported && showListenPrompt && <div>
          <div className="top-[50px] right-10 z-10 relative">
            <div className="right-0 absolute">
              <div className={`w-0 h-0 relative m-auto left-3 border-l-8 border-b-8 border-r-8 border-l-transparent border-r-transparent border-b-neutral-90`} />
              <div className="bg-neutral-90 p-0.5 px-2.5 rounded w-14 text-white text-sm text-center">
                {t('listen')}
              </div>
            </div>
          </div>
        </div>}
      <SimplePostCard containerStyle={[COMMON_STYLES.fullWidth, COMMON_STYLES.p1]} createdAt={relativeTime(created_at)} isFollowed={isFollowed} name={head?.name || ''} label={head?.label || ''} labelTextColor={head?.color?.text} likeCount={likeCount} commentCount={commentCount} isLiked={isLiked} isVerified={head?.is_verified} imageUrl={head?.display_photo || ''} tags={tags} fallBackUserImageOrIcon={<div className="mr-1 rounded-lg w-10 h-10">
            <UserVerifiedImage alt={head?.name || ''} fallbackImageClassName="rounded-lg" />
          </div>} onChipClick={handleTagClick} onChannelClick={handleChannelClick} onCardClick={handleCardClick} onFollowUnfollowBtnClick={toggleFollowBtn} onShareClick={() => handleShare(post_type)} onLikeClick={toggleLikeBtn} onCommentClick={onCommentClick} showSpeaker={true} onSpeakerClick={handleTextToSpeech} onThreeDotClick={() => null} data-sentry-element="SimplePostCard" data-sentry-source-file="DehaatFeedCard.tsx">
        <div className="flex flex-col md:h-auto cursor-pointer" data-testid="media-card">
          <h1 className="text-2xl leading-[1.2]">{name}</h1>
          <div className="flex justify-center rounded-lg">
            <MediaCardV2 attachments={attachments || []} data-sentry-element="MediaCardV2" data-sentry-source-file="DehaatFeedCard.tsx" />
          </div>
          <div ref={contentRef} className={`my-4 ${styles.dehaat_feed_content}`} dangerouslySetInnerHTML={{
          __html: content
        }} />
        </div>
      </SimplePostCard>
      <BottomDrawer show={isDrawerOpen} onClose={toggleDrawer} data-sentry-element="BottomDrawer" data-sentry-source-file="DehaatFeedCard.tsx">
        <LoginDrawer data-sentry-element="LoginDrawer" data-sentry-source-file="DehaatFeedCard.tsx" />
      </BottomDrawer>
      <ErrorModal {...errorModalState} onClose={hideErrorModal} data-sentry-element="ErrorModal" data-sentry-source-file="DehaatFeedCard.tsx">
        <Okay handleClick={hideErrorModal} data-sentry-element="Okay" data-sentry-source-file="DehaatFeedCard.tsx" />
      </ErrorModal>
    </div>;
};
export default DehaatFeedCard;